export default {
    // Eddie chat -- artifact renderers
    artifact: {
        download: 'Download',
        copy: 'Copy',
        copied: 'Copied',
        copy_link: 'Copy link',
        share: 'Share',
        share_tweet: 'Post on X',
        share_linkedin: 'Post to LinkedIn',
        share_facebook: 'Post to Facebook',
        share_threads: 'Post to Threads',
        more: 'More actions',
        open: 'Open',
        add_to_folder: 'Add to folder',
        add_to_sales_materials: 'Add to Sales Team Materials',
        rename: 'Rename',
        delete: 'Delete',
        edit: 'Edit',
        regenerate: 'Regenerate',
        retry: 'Try again',
        generating: 'Generating…',
        creating_image: 'Creating image…',
        expand: 'View full size',
        close: 'Close',
        previous_image: 'Previous image',
        next_image: 'Next image',
        draft: 'Draft',
        draft_sub: 'Long-form draft',
        failed: "Couldn't generate this. Try again.",
        play: 'Play',
        slide_count: '{count} slides',
        page_count: '{count} pages',
        ai_generated: 'AI-generated',
    },
    // Eddie chat -- approval-gate card (T21)
    approval: {
        heading: 'Approval needed',
        action_label: 'Action',
        args_label: 'Parameters',
        approve: 'Approve',
        edit: 'Edit',
        reject: 'Reject',
        respond: 'Respond',
        edit_heading: 'Edit action parameters',
        edit_placeholder: 'Paste or type the updated JSON parameters...',
        edit_apply: 'Apply edit',
        message_placeholder: 'Add a message...',
        cancel: 'Cancel',
    },
    // Left rail sidebar -- organization label + sessions list
    sidebar: {
        organization: 'Organization',
        open_sessions: 'Open sessions',
        close_sessions: 'Close sessions',
    },
    // Edit-direction checklist panel (mobile open/close affordances)
    checklist: {
        open: 'Open checklist',
        open_short: 'Checklist',
        close: 'Close checklist',
    },
    // Session picker (T20) -- lists org-shared sessions for a content item
    session_picker: {
        heading: 'Your chats',
        new_task: 'Create something new',
        new_task_aria: 'Create something new',
        list_aria: 'Existing chats',
        open_session_aria: 'Open session: {title}',
        delete_session_aria: 'Delete session: {title}',
        rename_session_aria: 'Rename session: {title}',
        rename_input_aria: 'Session title',
        delete_confirm_title: 'Delete this chat?',
        delete_confirm_body: 'This permanently removes the chat and any assets it generated. This can\'t be undone.',
        delete_confirm_cancel: 'Cancel',
        delete_confirm_confirm: 'Delete chat',
        loading_aria: 'Loading chats',
        untitled_session: 'Untitled session',
        // Row label: stable intent on the left, what the chat is about on the
        // right. Keeps same-type chats distinguishable without letting the
        // agent's shifting working_title rename the row.
        label_with_intent: '{intent} · {detail}',
        submitted: 'Submitted',
        in_progress: 'In progress',
        empty: 'Your next creation starts here',
        empty_sub: 'Pick a task above to get going. It stays saved here for your whole team.',
        just_now: 'Just now',
        minutes_ago: '{n}m ago',
        hours_ago: '{n}h ago',
        yesterday: 'Yesterday',
        days_ago: '{n}d ago',
    },
    // Thumbs up/down under the agent's answers.
    feedback: {
        helpful: 'Helpful',
        not_helpful: 'Not helpful',
        dialog_title: 'What went wrong?',
        dialog_placeholder: 'Tell us what you expected instead.',
        send: 'Send',
        // Backs out of a thumbs down before it is filed. Nothing is recorded
        // until Send, so this discards rather than deletes.
        cancel: 'Cancel',
        reasons: {
            hallucination: 'Hallucination',
            incorrect_info: 'Incorrect information',
            ignored_instructions: 'Did not follow instructions',
            off_brand: 'Off-brand voice or style',
            incomplete: 'Incomplete',
            too_slow: 'Took too long',
            other: 'Something else',
        },
        // Offered after a thumbs up is already saved, so it asks rather than
        // requires. Keys match the stored codes.
        positive_prompt: 'What worked?',
        // Asked once the conversation has produced something, so there is
        // actually an outcome to judge. Phrased about the whole conversation
        // rather than the last answer, which is what a customer who just got a
        // clip or a blog is thinking about.
        conversation_prompt: 'Did this conversation help you?',
        conversation_positive_prompt: 'What worked?',
        conversation_thanks: 'Thanks for telling us.',
        // These name the VALUE the work had, not how the model behaved. Accurate
        // / complete / fast read as a report card: an answer can be all three and
        // still need a rewrite before anyone can use it. No "Something else"
        // here, unlike `reasons` above, because a tag on praise is optional and
        // an untagged thumbs up already means exactly that.
        positive_reasons: {
            good_saved_time: 'Saved me time',
            good_ready_to_use: 'Ready to use',
            good_on_brand: 'Sounded like us',
            good_followed_direction: 'Nailed the brief',
        },
    },
    // Intent-driven entry CTAs (T20) -- shown when no sessions exist
    entry: {
        heading: 'What would you like to do?',
        subheading: 'Choose a task to get started.',
        hero: 'Multiply this into content you can share',
        hero_2: 'Turn this into everything you need to share',
        hero_3: 'One video, a dozen ways to share it',
        hero_4: 'Everything this content could become',
        hero_5: 'Give this a second life, everywhere',
        composer_placeholder: 'Try "Cut a 60-second version for LinkedIn"...',
        composer_placeholder_dynamic: 'Try "{title}"...',
        grid_aria: 'Suggestions',
        recommendations_heading: 'Suggested from this content',
        recommendations_aria: 'Recommendations from this content',
        recommendations_loading: 'Crafting content recommendations...',
        preparing_title: 'Almost ready to create',
        preparing_sub: 'Understanding your content',
        // Shown INSTEAD of the spinner when the pipeline has stopped. Each says
        // which of three genuinely different things happened, because the old
        // scene rendered all of them as the same endless sweep.
        preparing_stalled: 'This has stopped moving. Nothing has happened for a while.',
        preparing_stalled_at: 'Stopped while {stage}. Nothing has happened for a while.',
        preparing_failed: "We couldn't finish analyzing this one.",
        preparing_failed_reason: "We couldn't finish analyzing this one: {reason}",
        preparing_never_started: "We never started analyzing this one — it looks like it was never queued.",
        preparing_recovering: "It didn't start on its own, so we restarted it for you. This usually takes a couple of minutes.",
        // The action for the three stuck states above. Older content predates the
        // analysis pipeline (or lost its dispatch), and until this existed the
        // screen explained the problem with no way to act on it.
        reanalyze_cta: 'Analyze this media',
        reanalyze_starting: 'Starting…',
        reanalyze_started: 'Analysis started',
        preparing_tease_eyebrow: 'Tips',
        preparing_tip_describe_title: 'Describe what you want',
        preparing_tip_describe_desc: 'Plain English works fine, like "cut a 60-second version for LinkedIn." No forms to fill out.',
        preparing_tip_mention_title: 'Loop in a teammate',
        preparing_tip_mention_desc: "Use {'@'}Name in the composer. They'll see the same chat and can jump in on their own.",
        preparing_tip_saved_title: 'Nothing gets lost',
        preparing_tip_saved_desc: 'Every chat is saved here. Pick up exactly where you left off, whenever you come back.',
        preparing_tip_recommendations_title: 'Grounded suggestions',
        preparing_tip_recommendations_desc: "Once ready, you'll see specific ideas already pulled from this video, not generic prompts.",
        preparing_tip_media_title: 'Everything in one place',
        preparing_tip_media_desc: 'Clips, drafts, and images you create in chat all land in the Media tab automatically.',
        preparing_tip_multiplayer_title: 'Work together, live',
        preparing_tip_multiplayer_desc: "Invite a teammate into the same chat. You'll both see replies as they happen.",
        edit_request: 'Request an edit',
        edit_request_desc: 'Brief an editor and get a polished cut back.',
        edit_request_disabled: 'Open this from a specific piece of content to request an edit.',
        edit_request_not_ready: 'Still preparing this content — you\'ll be able to request an edit shortly.',
        make_clips: 'Clip Builder',
        make_clips_desc: 'Turn the best moments into short, shareable clips.',
        build_clips: 'Clip builder',
        build_clips_desc: 'Pick the exact moments and aspect ratios, and we cut to them.',
        write_blog: 'Write an article',
        write_blog_desc: 'A blog, case study, or training doc from this content.',
        write_social: 'Write social posts',
        write_social_desc: 'Ready-to-post captions and copy for your channels.',
        create_podcast: 'Create a podcast',
        create_podcast_desc: 'Repackage this as a podcast episode.',
        generate_image: 'Generate an image',
        generate_image_desc: 'A custom image drawn from this content.',
        create_infographic: 'Create an infographic',
        create_infographic_desc: 'Explain this content in a designed layout.',
        general_chat: 'Ask anything',
        general_chat_desc: 'Chat about this content.',
        use_edit_form: 'Use Edit Request Form',
        // R6-F-001: shown when the content type isn't supported by Eddie yet
        // (e.g. a podcast recording) — instead of dead-ending into a broken
        // intent surface.
        unsupported_title: "Chat isn't available for this content yet",
        unsupported_body:
            'Chat works on videos and media files. Support for this content type is coming soon.',
    },
    // Composer hint line (below the input)
    composer: {
        mention_hint: "Multi-user mode: Messages go to the AI by default. Use {'@'}Name to message a teammate.",
        attach: 'Attach a brief or document',
        remove_attachment: 'Remove {name}',
    },
    // Multiplayer presence roster (T22) -- shows who else is in the session
    presence: {
        roster_aria: 'Session participants',
        prompting: '{name} is prompting...',
        // A bare `@` is vue-i18n's linked-message trigger char — it breaks
        // compilation of the WHOLE message (including `{name}`) unless
        // escaped as a literal via `{'@'}` (see simple-login.js precedent).
        one_other: "{name} is here — mention with {'@'}",
        n_others: "{count} teammates here — mention with {'@'}",
    },
    // @-mention dropdown in the composer -- scoped to live session participants
    mention: {
        aria_label: 'Mention a teammate',
        no_matches: 'No teammates match "{query}"',
    },
    // Session busy banner (T22) -- shown when session is locked by another user
    busy: {
        banner_label: '{name} is editing this session',
        stopped_label: 'Stopped at your request.',
        start_new_edit_request: 'Start a new edit request',
    },
    // Clip Builder -- the scripted (no-LLM) interview behind the build_clips chip.
    // Copy is regenerated from these keys on rehydration; the server stores only
    // the structured answers, never prose.
    clip_builder: {
        opener: "Let's cut clips to your exact moments, styled with your brand.",
        moments_prompt: 'Which moments should I cut? Type ranges like "2:14 to 2:41" (several are fine), or let me pick for you.',
        choose_for_me: 'Choose for me',
        choose_for_me_desc: 'I scan the video and pick the strongest moments.',
        count_prompt: 'How many clips should I pick? 4 works well for most videos.',
        ratios_prompt: 'Which shapes do you need? Landscape and portrait cover most channels.',
        finish_prompt: 'How should these clips end?',
        finish_brand: "Use my brand's setting",
        finish_hard_cut: 'Hard cut (recommended)',
        finish_fade_black: 'Fade to black',
        finish_fade_white: 'Fade to white',
        ratio_landscape: 'Landscape',
        ratio_landscape_desc: '16:9, for YouTube and the web.',
        ratio_portrait: 'Portrait',
        ratio_portrait_desc: '9:16, for Reels, TikTok, and Shorts.',
        ratio_square: 'Square',
        ratio_square_desc: '1:1, for feeds.',
        style_intro_line: "Here's how your clips will be styled, straight from your Styles tab:",
        style_logo_on: 'logo on, {corner}',
        style_logo_off: 'no logo',
        style_captions_on: 'captions in {preset} style, {font}',
        style_captions_off: 'no captions',
        style_safe_zone_on: '; kept inside the platform safe zone',
        style_stings: 'With your {parts}.',
        style_intro: 'intro',
        style_outro: 'outro',
        style_none: 'No clip style is set up yet, so these clips will use the default look. You can style future clips under [Brand Brain, Assets](/brand-brain?tab=assets).',
        confirm_prompt: 'Cut the clips with this look? Styling changes live in the Styles tab.',
        confirm_yes: 'Cut my clips',
        confirm_no: 'Not now',
        canceled: 'No problem. Reopen Clip builder whenever you are ready.',
        dispatched: 'On it. Cutting your clips now; this can take a few minutes.',
        done: 'Your clips are ready. They are also saved in the Media tab.',
        failed_qc: 'No clip passed the cut checks ({reason}). Try a slightly different window; a moment that starts and ends on clean speech works best.',
        failed: "Couldn't render the clips: {reason}",
        unavailable: "Clip Builder isn't available right now because the render service didn't respond. Nothing was sent anywhere else.",
        unavailable_generic: "Something went wrong reaching the render service. Nothing was sent anywhere else.",
        retry_prompt: 'Want me to try again?',
        retry: 'Try again',
        busy: 'A clip job is already running in this chat. Wait for it to finish before starting another.',
        taking_long: 'Rendering is taking longer than usual. Your clips will land in the Media tab when they finish.',
        use_options: 'Pick one of the options above to continue.',
        flow_done: 'This clip task is finished. Start a new task from the picker to make more clips.',
        err_none: 'I couldn\'t find a time range in that. Type it like "2:14 to 2:41".',
        err_inverted: 'That range ends before it starts ({range}). Type it like "2:14 to 2:41".',
        err_too_short: 'Each clip needs at least 12 seconds ({range}). Widen the window and try again.',
        err_out_of_bounds: 'That range runs past the end of this video ({range}).',
    },
    // Job progress rows (T22) -- per-job progress and artifact display
    job_progress: {
        queued: 'Queued...',
        running: '{kind} in progress',
        processing: 'Processing...',
        complete: '{kind} complete',
        failed: '{kind} failed',
    },
    // Phases narrated on the image-generation card while a render is in flight.
    // Used only when the agent has streamed no progress message of its own.
    image_generation: {
        creating: 'Creating image',
        sketching: 'Sketching it out',
        blocking: 'Blocking in the composition',
        detail: 'Adding detail',
        finishing: 'Finishing touches',
    },
    // Message bubble attribution (T22) -- remote user message label
    message: {
        from_sender: 'from {name}',
    },
    // Media library (T16) -- grid with category filters and pagination
    media: {
        title: 'Media',
        subtitle: '{count} items · everything created from chat, plus what you upload',
        empty: 'Your ideas, made real',
        empty_sub: 'Describe what you need in chat. The media you create gathers here, ready to use.',
        empty_cta: 'Start creating',
        upload: 'Upload',
        upload_hint: 'Upload media from your device',
        uploading: 'Uploading {count} file(s)…',
        upload_drop: 'Drop to upload',
        upload_drop_hint: 'Video, image, audio or document. Up to {max} at a time.',
        uploaded: '{count} file(s) uploaded',
        upload_partial: '{count} uploaded, {failed} could not be uploaded',
        upload_failed: 'Could not upload. Please try again.',
        upload_too_large: 'That file is too large to upload here. Try a smaller file.',
        upload_too_many: 'You can upload up to {max} files at a time.',
        untitled: 'Untitled',
        more: 'More actions',
        delete_confirm: 'Delete "{title}"? This removes it from the media library.',
        delete_confirm_bulk: 'Delete {count} selected items? This removes them from the media library.',
        deleted: 'Media deleted',
        deleted_bulk: '{count} items deleted',
        delete_failed: 'Could not delete this media. Please try again.',
        rename_title: 'Rename media',
        rename_placeholder: 'Media name',
        rename_save: 'Save',
        renamed: 'Renamed',
        rename_failed: 'Could not rename this media. Please try again.',
        add_to_folder_soon: 'Adding media to folders is coming soon.',
        added_to_folder: 'Added {count} to "{folder}"',
        add_to_sales_materials_failed: "This item isn't linked to a piece of content, so it can't be added to Sales Team Materials.",
        add_to_folder_failed: 'Could not add to folder. Please try again.',
        add_to_folder_nothing: "Nothing was added — these items have no file to put in a folder.",
        selected: '{count} selected',
        clear_selection: 'Clear selection',
        select_item: 'Select item',
        folder_picker: {
            title: 'Add {count} to folder',
            search: 'Search folders',
            loading: 'Loading folders…',
            none: 'No folders yet',
            in_parent: 'in {parent}',
            new: 'Or create a new folder',
            confirm: 'Add to folder',
        },
        filters: {
            all: 'All',
            audio: 'Audio',
            video: 'Videos',
            image: 'Images',
            document: 'Documents',
        },
        sort: {
            label: 'Sort by',
            newest: 'Newest',
            oldest: 'Oldest',
            name_asc: 'Name (A–Z)',
            name_desc: 'Name (Z–A)',
        },
        date: {
            today: 'Today',
            yesterday: 'Yesterday',
            days_ago: '{count}d ago',
        },
        pagination: {
            label: 'Pagination',
            prev: 'Previous page',
            next: 'Next page',
        },
    },
    // Error rows and job failure states (T19) -- friendly copy + retry control
    errors: {
        /** Default fallback shown when no agent-supplied message is available. */
        default: 'Something went wrong, try again later.',
        /** Retry button label on recoverable error rows. */
        retry: 'Try again',
        /** Shown when a job/artifact fails without a specific agent message. */
        job_failed: 'This job couldn\'t complete. Try again.',
    },
}
